// Lokasi: the list page and its three views (Daftar, Peta, Jaringan), the form, and the location page.
import type { LocationStatus } from '@lib/api/locations';
import type { VillageKind } from '@lib/api/regions';

export const LOCATIONS = {
  title: 'Titik Distribusi',
  sub: (warehouse: string) => `${warehouse}, SPBG, Hub, dan Bengkel Uji: tempat tabung disimpan dan dibaca Reader.`,
  add: 'Tambah titik distribusi',
  addWarehouse: (warehouse: string) => `Tambah ${warehouse}`,
  loading: 'Memuat titik distribusi…',
  noun: 'titik distribusi',
  noWarehouse: (warehouse: string) => ({ lead: 'Belum ada titik distribusi berjenis', tail: `Tabung hanya bisa didaftarkan dari Reader di ${warehouse}.` }),
  columns: { name: 'Titik Distribusi', type: 'Jenis', parent: 'Induk', stock: 'Stok', readers: 'Reader', lastArrival: 'Terakhir masuk' },
  noReader: 'Belum ada',
  readers: (online: number, total: number) => `${online} dari ${total} terhubung`,
  noParent: 'Tanpa induk',
  offNetwork: 'Di luar jaringan',
  /** a hub's stock: how many are full (ready to deliver) and empty */
  fill: (full: string, empty: string) => `isi ${full} · kosong ${empty}`,
  empty: {
    warehouse: (warehouse: string) => ({ title: 'Belum ada titik distribusi', text: `Mulai dari ${warehouse}, lalu tambahkan SPBG dan Hub tujuan kiriman.` }),
    manager: { title: 'Belum ada titik distribusi', text: 'Tambahkan SPBG dan Hub di wilayah Anda.' },
    viewer: { title: 'Belum ada titik distribusi', text: 'Administrator belum menambahkan titik distribusi.' },
  },
  noMatch: { title: 'Tidak ada titik distribusi yang cocok', text: 'Ubah kata kunci, jenis, wilayah, atau status.' },
} as const;

/** Daftar | Peta | Jaringan */
export const LOCATION_VIEWS = { label: 'Tampilan Titik Distribusi', list: 'Daftar', map: 'Peta', tree: 'Jaringan' } as const;

/** Toolbar filters (menus on laptops, a sheet on phones). */
export const LOCATION_FILTERS = {
  search: 'Cari titik distribusi',
  searchPlaceholder: 'Cari nama atau kode…',
  searchPlaceholderShort: 'Cari titik distribusi…',
  type: 'Jenis',
  typesChosen: (n: number) => `${n} jenis`,
  region: 'Wilayah',
  province: 'Provinsi',
  allProvinces: 'Semua provinsi',
  provinceSearch: 'Cari provinsi…',
  noProvince: 'Tidak ada provinsi yang cocok.',
  regency: 'Kabupaten/kota',
  allRegencies: 'Semua kabupaten/kota',
  regencySearch: 'Cari kabupaten/kota…',
  noRegency: 'Tidak ada kabupaten/kota yang cocok.',
  regencyFailed: 'Kabupaten/kota belum bisa dimuat.',
  district: 'Kecamatan',
  allDistricts: 'Semua kecamatan',
  districtSearch: 'Cari kecamatan…',
  noDistrict: 'Tidak ada kecamatan yang cocok.',
  village: 'Desa/kelurahan',
  allVillages: 'Semua desa/kelurahan',
  villageSearch: 'Cari desa/kelurahan…',
  noVillage: 'Tidak ada desa/kelurahan yang cocok.',
  districtChosen: 'Kecamatan terpilih',
  villageChosen: 'Desa/kelurahan terpilih',
  areaLoading: 'Memuat daftar…',
  areaFailed: 'Daftar belum bisa dimuat.',
  areaNone: 'Data kecamatan dan desa belum tersedia untuk wilayah ini.',
  /** a parent with more than one page: the server searches from REGION_LIST.searchMin letters */
  areaTypeMore: (min: number) => `Ketik minimal ${min} huruf untuk mencari yang lain.`,
  retry: 'Coba lagi',
  status: 'Status',
  statusesChosen: (n: number) => `${n} status`,
  clearChoice: 'Kosongkan pilihan',
} as const;

/** A village's kind, from its Kemendagri code. */
export const VILLAGE_KIND: Record<VillageKind, string> = { URBAN_VILLAGE: 'Kelurahan', VILLAGE: 'Desa', CUSTOMARY_VILLAGE: 'Desa adat' };

export const LOCATION_STATUS: Record<LocationStatus, { label: string; sub: string }> = {
  reader_offline: { label: 'Reader terputus', sub: 'Ada reader yang tidak terhubung' },
  no_reader: { label: 'Belum ada Reader', sub: 'Belum ada Reader terpasang di titik ini' },
  stock_empty: { label: 'Stok kosong', sub: 'Tidak ada tabung tercatat' },
  off_network: { label: 'Di luar jaringan', sub: 'Hub tanpa SPBG induk' },
};

/** The ⋯ menu of a row and the actions of the location page. */
export const LOCATION_ACTIONS = {
  menu: (name: string) => `Tindakan untuk ${name}`,
  open: 'Buka',
  edit: 'Ubah',
  photos: 'Foto',
  remove: 'Hapus',
  confirmTitle: (name: string) => `Hapus ${name}?`,
  confirmBody: 'Titik Distribusi ini dihapus permanen beserta fotonya dan tidak dapat dikembalikan. Titik Distribusi yang masih memiliki Reader, masih menjadi lokasi tugas petugas, atau sudah tercantum di Surat Jalan tidak dapat dihapus.',
  confirmLabel: 'Hapus titik distribusi',
  removed: (name: string) => `${name} dihapus`,
  someChildren: 'beberapa',
} as const;

/** Peta: the whole scope on one map. */
export const LOCATION_MAP = {
  label: 'Peta Titik Distribusi',
  failedTitle: 'Peta belum bisa dimuat',
  stale: 'Peta belum diperbarui: Titik Distribusi yang tampil mungkin belum sesuai filter.',
  noneTitle: 'Belum ada Titik Distribusi berkoordinat',
  noneText: 'Isi lintang dan bujur pada data Titik Distribusi agar tampil di peta.',
  noMatchTitle: 'Tidak ada Titik Distribusi yang cocok di peta',
  unplaced: (n: string) => `${n} Titik Distribusi belum memiliki koordinat`,
  shown: (n: string) => `${n} Titik Distribusi di peta`,
} as const;

/** Jaringan: the distribution network as a tree. */
export const LOCATION_TREE = {
  label: 'Jaringan distribusi',
  hint: 'Gunakan panah untuk berpindah dan membuka cabang, Enter untuk membuka Titik Distribusi.',
  loadMoreCount: (shown: string, total: string) => `Muat lebih banyak (${shown} dari ${total})`,
  loading: 'Memuat bawahan…',
  childFailed: 'Bawahan belum bisa dimuat.',
  own: 'stok',
  network: 'jaringan',
  networkTitle: 'Stok Titik Distribusi ini dan semua Titik Distribusi di bawahnya',
  children: (n: number) => `${n} bawahan`,
  context: 'Induk dari hasil pencarian',
  outsideScope: 'Bawahan di luar wilayah Anda',
  stockEmpty: 'Stok kosong',
  readerOffline: 'Reader terputus',
  empty: { title: 'Belum ada jaringan', text: 'Titik Distribusi muncul di sini setelah ditambahkan.' },
  matches: (n: string) => `${n} titik distribusi cocok, ditampilkan dengan induknya`,
} as const;

/** The add/edit drawer. */
export const LOCATION_FORM = {
  addTitle: 'Tambah titik distribusi',
  discardTitle: 'Buang isian?',
  discardBody: 'Perubahan pada formulir ini belum disimpan.',
  discard: 'Buang isian',
  keepEditing: 'Lanjut mengisi',
  roughFix: 'Perkiraan kasar dari perangkat. Geser pin ke posisi yang tepat sebelum menyimpan.',
  editTitle: (name: string) => `Ubah ${name}`,
  submitAdd: 'Tambah titik distribusi',
  submitEdit: 'Simpan perubahan',
  added: (name: string) => `${name} ditambahkan`,
  updated: 'Titik Distribusi diperbarui',
  sections: { identity: 'Identitas', point: 'Koordinat', network: 'Jaringan' },
  labels: { type: 'Jenis', name: 'Nama', code: 'Kode', point: 'Koordinat', lat: 'Lintang', lng: 'Bujur', province: 'Provinsi', regency: 'Kabupaten/kota', district: 'Kecamatan', village: 'Desa/kelurahan', address: 'Alamat' },
  placeholders: { name: 'Hub Desa Ragunan', code: 'HUB-RGN', point: '-6.2915, 106.8020', lat: '-6.2915', lng: '106.8020', address: 'Jl. RS Fatmawati Raya No. 15, Cilandak' },
  warehouseHelp: 'Hanya boleh satu. Tabung didaftarkan dan kiriman pertama berangkat dari sini.',
  codeHelp: 'Unik. Huruf, angka, - dan _.',
  codeSuggested: 'Dibuat dari jenis dan nama. Boleh diubah.',
  codeSuggesting: 'Membuat kode…',
  pointHelp: 'Tempel dari Google Maps (klik kanan titiknya, lalu klik angka koordinat), klik peta, atau geser pin.',
  separate: 'Isi lintang dan bujur terpisah',
  together: 'Isi dalam satu kolom',
  useMine: 'Pakai lokasi saya',
  locating: 'Mencari lokasi…',
  located: (m: string) => `Titik diambil dari lokasi perangkat (akurasi ± ${m} m). Geser pin bila belum tepat.`,
  clearPoint: 'Hapus titik',
  mapLabel: 'Peta titik lokasi. Klik peta untuk menaruh pin.',
  pinLabel: 'Titik lokasi. Geser, atau pakai tombol panah, untuk memindahkan.',
  mapHint: 'Klik peta untuk menaruh pin',
  zoomIn: 'Perbesar',
  zoomOut: 'Perkecil',
  regencyKind: { REGENCY: 'Kabupaten', CITY: 'Kota' },
  regencySearch: 'Cari kabupaten/kota…',
  regencyNoMatch: (q: string) => `Tidak ada kabupaten/kota yang cocok dengan “${q}”.`,
  regencyNone: 'Belum ada data kabupaten/kota.',
  provincePick: 'Pilih provinsi…',
  regencyFirst: 'Pilih provinsi dulu',
  districtFirst: 'Pilih kabupaten/kota dulu',
  villageFirst: 'Pilih kecamatan dulu',
  hubVillageHelp: 'Hub melayani warga di desa/kelurahan ini.',
  villageOptionalHelp: 'Boleh dikosongkan.',
  clearArea: 'Kosongkan kecamatan dan desa',
  provincesFailed: (detail: string) => `Daftar provinsi belum termuat. ${detail}`,
  errors: {
    name: 'Nama wajib diisi.',
    code: 'Kode wajib diisi.',
    codeFormat: 'Hanya huruf, angka, - dan _.',
    province: 'Pilih provinsi.',
    regency: 'Pilih kabupaten/kota.',
    district: 'Pilih kecamatan tempat Hub berada.',
    village: 'Pilih desa/kelurahan tempat Hub berada.',
    villageOrClear: 'Pilih desa/kelurahannya, atau kosongkan kecamatan.',
    point: 'Tulis lintang dan bujur, contoh -6.2915, 106.8020.',
    lat: 'Lintang berupa angka, contoh -6.2915.',
    lng: 'Bujur berupa angka, contoh 106.8020.',
    pair: 'Isi lintang dan bujur bersama.',
    outside: 'Titik ini di luar Indonesia. Periksa lintang dan bujurnya (lintang −11,5 s.d. 6,5; bujur 94,5 s.d. 141,5).',
    parent: (type: string) => `Pilih ${type} induknya.`,
  },
  geo: {
    denied: 'Izin lokasi ditolak. Izinkan akses lokasi untuk situs ini di pengaturan browser, atau pilih titik di peta.',
    unavailable: 'Lokasi perangkat belum bisa ditentukan. Coba lagi di tempat terbuka, atau pilih titik di peta.',
    timeout: 'Lokasi perangkat terlalu lama ditemukan. Coba lagi, atau pilih titik di peta.',
    unsupported: 'Browser ini tidak memberi lokasi perangkat. Tempel koordinat atau pilih titik di peta.',
    outside: 'Lokasi perangkat Anda di luar Indonesia, jadi tidak dipakai. Pilih titik di peta.',
  },
} as const;

/** The location page. */
export const LOCATION_DETAIL = {
  missing: 'Titik Distribusi tidak ditemukan',
  back: 'Kembali ke daftar Titik Distribusi',
  code: 'Kode',
  parent: 'Induk',
  childCount: (n: string) => `${n} bawahan`,
  /** the village (and its district) a location lies in: always shown for a hub */
  area: (village: string, district: string | null) => (district ? `Desa/kelurahan ${village}, Kecamatan ${district}` : `Desa/kelurahan ${village}`),
  childrenOutside: 'Bawahan di luar wilayah Anda',
  summary: { title: 'Ringkasan 14 hari', stock: 'Stok sekarang', stockWarehouse: 'Di gudang sekarang', network: (n: string) => `jaringan ${n}`, fill: (full: string, empty: string) => `isi ${full} · kosong ${empty}`, in: 'Masuk 14 hari', out: 'Keluar 14 hari', overdue: 'Lewat jadwal uji', chart: 'Tabung masuk dan keluar per hari, 14 hari', seriesIn: 'Masuk', seriesOut: 'Keluar', quiet: 'Belum ada pergerakan 14 hari terakhir' },
  seeAll: (n: string) => `Lihat semua (${n})`,
  seeAllPlain: 'Lihat semua',
  children: {
    title: 'Bawahan',
    count: (n: string) => `${n} Titik Distribusi`,
    attention: (n: string) => `${n} perlu dicek`,
    drawerTitle: (name: string) => `Bawahan ${name}`,
    search: 'Cari bawahan',
    searchPlaceholder: 'Cari nama atau kode…',
    all: 'Semua',
    needsLook: 'Perlu dicek',
    filterLabel: 'Tampilkan',
    empty: 'Belum ada Titik Distribusi bawahan',
    noMatch: 'Tidak ada bawahan yang cocok',
  },
  stock: {
    columns: { serial: 'Nomor seri', owner: 'Pemilik', retest: 'Uji ulang', lastSeen: 'Terakhir terbaca' },
    overdue: (month: string) => `Lewat ${month}`,
    title: 'Tabung di Titik Distribusi ini',
    drawerTitle: (name: string) => `Tabung di ${name}`,
    search: 'Cari nomor seri',
    searchPlaceholder: 'Cari nomor seri…',
    openAll: 'Buka di daftar tabung',
    noun: 'tabung',
    noMatch: 'Tidak ada tabung yang cocok',
    emptyShort: 'Belum ada tabung tercatat di sini.',
    emptyWarehouse: 'Tabung yang didaftarkan di reader gudang langsung tercatat di sini.',
  },
  movements: {
    in: (from: string) => `Masuk dari ${from}`,
    out: (to: string) => `Keluar ke ${to}`,
    title: 'Pergerakan',
    drawerTitle: (name: string) => `Pergerakan ${name}`,
    registered: 'Didaftarkan di sini',
    elsewhere: 'lokasi lain',
    noun: 'pergerakan',
    emptyShort: 'Belum ada tabung masuk atau keluar.',
  },
  map: { title: 'Peta', label: (name: string) => `Titik Distribusi ${name}`, noneText: 'Isi koordinat agar tampil di peta.', add: 'Isi koordinat' },
  readers: {
    title: 'Reader di Titik Distribusi ini',
    meta: (tags: string, heard: string) => `${tags} tag di depan reader · kabar ${heard}`,
    emptyShort: 'Belum ada Reader di Titik Distribusi ini.',
    place: 'Tempatkan gateway',
    down: (n: string) => `${n} terputus`,
    drawerTitle: (name: string) => `Reader di ${name}`,
    noun: 'reader',
  },
  shipments: {
    title: 'Surat Jalan terakhir',
    all: 'Semua',
    to: (name: string) => `Ke ${name}`,
    from: (name: string) => `Dari ${name}`,
    cylinders: (n: number) => `${n} tabung`,
    emptyShort: 'Belum ada Surat Jalan dari atau ke Titik Distribusi ini.',
  },
} as const;

/** The location search picker (a parent, a Surat Jalan's destination, a filter). */
export const LOCATION_PICKER = {
  placeholder: 'Cari nama atau kode titik distribusi…',
  noMatch: (q: string) => `Tidak ada Titik Distribusi yang cocok dengan “${q}”.`,
  none: 'Belum ada Titik Distribusi.',
  narrow: 'Ketik nama atau kode untuk mempersempit.',
} as const;

/** An active Hub's standing on its page: status, agreement, re-inspection, sanctions (Phase 2). */
export const HUB_STANDING = {
  title: 'Status Hub',
  loading: 'Memuat status Hub…',
  failed: 'Status Hub tidak dapat dimuat.',
  agreement: 'Nomor PKS',
  agreementUntil: 'PKS berlaku sampai',
  noAgreement: 'Belum ada PKS tercatat',
  agreementNth: (n: number) => `PKS ke-${n}`,
  expiresSoon: 'Segera berakhir',
  expired: 'Sudah berakhir',
  landUntil: 'Sewa/pinjam pakai lahan sampai',
  lastInspection: 'Pemeriksaan ulang terakhir',
  reinspection: 'Pemeriksaan ulang jatuh tempo',
  application: 'Nomor pendaftaran',
  sanctions: 'Riwayat sanksi dan pemulihan',
  noSanctions: 'Belum ada sanksi.',
  add: 'Catat sanksi',
  dialogTitle: 'Catat sanksi atau pemulihan Hub',
  kind: 'Jenis sanksi',
  kindPick: 'Pilih jenis sanksi',
  kindRequired: 'Pilih jenis sanksi.',
  reason: 'Alasan',
  reasonHelp: 'Tercatat di riwayat Hub dan log aktivitas.',
  reasonRequired: 'Alasan wajib diisi, minimal 5 karakter.',
  letter: 'Surat sanksi (opsional)',
  letterHelp: 'Pindaian surat yang telah ditandatangani, PDF atau foto.',
  effect: {
    WARNING_1: 'Surat peringatan pertama dicatat. Hub tetap beroperasi.',
    WARNING_2: 'Surat peringatan kedua dicatat. Pelanggaran berikutnya dapat berujung penghentian sementara.',
    SUSPEND: 'Hub dihentikan sementara: tidak dapat menjadi tujuan Surat Jalan distribusi. Akun Petugas Hub tetap aktif.',
    REINSTATE: 'Penghentian sementara diakhiri: Hub kembali dapat menerima Surat Jalan distribusi.',
    TERMINATE: 'Kemitraan Hub diputus secara permanen dan akun Petugas Hub dinonaktifkan. Tabung di Hub tetap dapat dikirim kembali ke SPBG atau Bengkel Uji.',
  },
  confirm: 'Simpan',
  done: 'Sanksi dicatat',
  terminateNational: 'Pemutusan kemitraan hanya dapat dilakukan oleh admin nasional.',
  by: (name: string) => `oleh ${name}`,
  // renewals
  renewAgreement: 'Perbarui PKS',
  renewAgreementTitle: 'Catat PKS baru',
  agreementNumber: 'Nomor PKS',
  signedOn: 'Tanggal penandatanganan',
  validUntil: 'Berlaku sampai',
  scan: 'Pindaian PKS bertanda tangan',
  scanHelp: 'Satu berkas PDF berisi semua halaman yang telah ditandatangani.',
  scanRequired: 'Unggah pindaian PKS.',
  numberRequired: 'Nomor PKS wajib diisi.',
  dateRequired: 'Lengkapi tanggal, bulan, dan tahun.',
  datesInvalid: 'Tanggal berlaku harus setelah tanggal penandatanganan.',
  agreementDone: 'PKS baru dicatat',
  renewLand: 'Perbarui sewa lahan',
  renewLandTitle: 'Perpanjangan sewa/pinjam pakai lahan',
  landValidUntil: 'Berlaku sampai',
  landFuture: 'Tanggal harus setelah hari ini.',
  landProof: 'Bukti perpanjangan',
  landProofHelp: 'Perjanjian sewa atau pinjam pakai yang baru, PDF atau foto.',
  landProofRequired: 'Unggah bukti perpanjangan.',
  landDone: 'Masa berlaku lahan diperbarui',
  upload: {
    choose: 'Pilih berkas',
    replace: 'Ganti',
    retry: 'Coba lagi',
    badType: 'Format berkas tidak didukung. Gunakan PDF, JPG, PNG, atau WebP.',
    photoOnly: 'Berkas ini harus berupa foto.',
    pdfOnly: 'Berkas ini harus berupa PDF.',
    pdfActive: 'Berkas PDF berisi skrip atau lampiran tersemat. Simpan ulang sebagai PDF biasa, lalu unggah kembali.',
    tooLarge: (max: string) => `Berkas terlalu besar. Maksimal ${max}.`,
    unreadable: 'Berkas tidak dapat dibaca. Pilih berkas lain.',
  },
} as const;
